/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectAddress,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";

export const UNSTAKE_GHOST_DISCRIMINATOR = new Uint8Array([
  252, 101, 172, 252, 1, 4, 12, 116,
]);

export function getUnstakeGhostDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    UNSTAKE_GHOST_DISCRIMINATOR,
  );
}

export type UnstakeGhostInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountStakingAccount extends string | AccountMeta<string> = string,
  TAccountStakingConfig extends string | AccountMeta<string> = string,
  TAccountStakingVault extends string | AccountMeta<string> = string,
  TAccountOwnerTokenAccount extends string | AccountMeta<string> = string,
  TAccountOwner extends string | AccountMeta<string> = string,
  TAccountTokenProgram extends string | AccountMeta<string> =
    "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountStakingAccount extends string
        ? WritableAccount<TAccountStakingAccount>
        : TAccountStakingAccount,
      TAccountStakingConfig extends string
        ? ReadonlyAccount<TAccountStakingConfig>
        : TAccountStakingConfig,
      TAccountStakingVault extends string
        ? WritableAccount<TAccountStakingVault>
        : TAccountStakingVault,
      TAccountOwnerTokenAccount extends string
        ? WritableAccount<TAccountOwnerTokenAccount>
        : TAccountOwnerTokenAccount,
      TAccountOwner extends string
        ? WritableSignerAccount<TAccountOwner> &
            AccountSignerMeta<TAccountOwner>
        : TAccountOwner,
      TAccountTokenProgram extends string
        ? ReadonlyAccount<TAccountTokenProgram>
        : TAccountTokenProgram,
      ...TRemainingAccounts,
    ]
  >;

export type UnstakeGhostInstructionData = { discriminator: ReadonlyUint8Array };

export type UnstakeGhostInstructionDataArgs = {};

export function getUnstakeGhostInstructionDataEncoder(): FixedSizeEncoder<UnstakeGhostInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([["discriminator", fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({ ...value, discriminator: UNSTAKE_GHOST_DISCRIMINATOR }),
  );
}

export function getUnstakeGhostInstructionDataDecoder(): FixedSizeDecoder<UnstakeGhostInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getUnstakeGhostInstructionDataCodec(): FixedSizeCodec<
  UnstakeGhostInstructionDataArgs,
  UnstakeGhostInstructionData
> {
  return combineCodec(
    getUnstakeGhostInstructionDataEncoder(),
    getUnstakeGhostInstructionDataDecoder(),
  );
}

export type UnstakeGhostAsyncInput<
  TAccountStakingAccount extends string = string,
  TAccountStakingConfig extends string = string,
  TAccountStakingVault extends string = string,
  TAccountOwnerTokenAccount extends string = string,
  TAccountOwner extends string = string,
  TAccountTokenProgram extends string = string,
> = {
  stakingAccount?: Address<TAccountStakingAccount>;
  stakingConfig?: Address<TAccountStakingConfig>;
  stakingVault?: Address<TAccountStakingVault>;
  ownerTokenAccount: Address<TAccountOwnerTokenAccount>;
  owner: TransactionSigner<TAccountOwner>;
  tokenProgram?: Address<TAccountTokenProgram>;
};

export async function getUnstakeGhostInstructionAsync<
  TAccountStakingAccount extends string,
  TAccountStakingConfig extends string,
  TAccountStakingVault extends string,
  TAccountOwnerTokenAccount extends string,
  TAccountOwner extends string,
  TAccountTokenProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: UnstakeGhostAsyncInput<
    TAccountStakingAccount,
    TAccountStakingConfig,
    TAccountStakingVault,
    TAccountOwnerTokenAccount,
    TAccountOwner,
    TAccountTokenProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  UnstakeGhostInstruction<
    TProgramAddress,
    TAccountStakingAccount,
    TAccountStakingConfig,
    TAccountStakingVault,
    TAccountOwnerTokenAccount,
    TAccountOwner,
    TAccountTokenProgram
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    stakingAccount: { value: input.stakingAccount ?? null, isWritable: true },
    stakingConfig: { value: input.stakingConfig ?? null, isWritable: false },
    stakingVault: { value: input.stakingVault ?? null, isWritable: true },
    ownerTokenAccount: {
      value: input.ownerTokenAccount ?? null,
      isWritable: true,
    },
    owner: { value: input.owner ?? null, isWritable: true },
    tokenProgram: { value: input.tokenProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Resolve default values.
  if (!accounts.stakingAccount.value) {
    accounts.stakingAccount.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([115, 116, 97, 107, 105, 110, 103]),
        ),
        getAddressEncoder().encode(expectAddress(accounts.owner.value)),
      ],
    });
  }
  if (!accounts.stakingConfig.value) {
    accounts.stakingConfig.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            115, 116, 97, 107, 105, 110, 103, 95, 99, 111, 110, 102, 105, 103,
          ]),
        ),
      ],
    });
  }
  if (!accounts.stakingVault.value) {
    accounts.stakingVault.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            115, 116, 97, 107, 105, 110, 103, 95, 118, 97, 117, 108, 116,
          ]),
        ),
        getAddressEncoder().encode(expectAddress(accounts.stakingConfig.value)),
      ],
    });
  }
  if (!accounts.tokenProgram.value) {
    accounts.tokenProgram.value =
      "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA" as Address<"TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.stakingAccount),
      getAccountMeta(accounts.stakingConfig),
      getAccountMeta(accounts.stakingVault),
      getAccountMeta(accounts.ownerTokenAccount),
      getAccountMeta(accounts.owner),
      getAccountMeta(accounts.tokenProgram),
    ],
    data: getUnstakeGhostInstructionDataEncoder().encode({}),
    programAddress,
  } as UnstakeGhostInstruction<
    TProgramAddress,
    TAccountStakingAccount,
    TAccountStakingConfig,
    TAccountStakingVault,
    TAccountOwnerTokenAccount,
    TAccountOwner,
    TAccountTokenProgram
  >);
}

export type UnstakeGhostInput<
  TAccountStakingAccount extends string = string,
  TAccountStakingConfig extends string = string,
  TAccountStakingVault extends string = string,
  TAccountOwnerTokenAccount extends string = string,
  TAccountOwner extends string = string,
  TAccountTokenProgram extends string = string,
> = {
  stakingAccount: Address<TAccountStakingAccount>;
  stakingConfig: Address<TAccountStakingConfig>;
  stakingVault: Address<TAccountStakingVault>;
  ownerTokenAccount: Address<TAccountOwnerTokenAccount>;
  owner: TransactionSigner<TAccountOwner>;
  tokenProgram?: Address<TAccountTokenProgram>;
};

export function getUnstakeGhostInstruction<
  TAccountStakingAccount extends string,
  TAccountStakingConfig extends string,
  TAccountStakingVault extends string,
  TAccountOwnerTokenAccount extends string,
  TAccountOwner extends string,
  TAccountTokenProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: UnstakeGhostInput<
    TAccountStakingAccount,
    TAccountStakingConfig,
    TAccountStakingVault,
    TAccountOwnerTokenAccount,
    TAccountOwner,
    TAccountTokenProgram
  >,
  config?: { programAddress?: TProgramAddress },
): UnstakeGhostInstruction<
  TProgramAddress,
  TAccountStakingAccount,
  TAccountStakingConfig,
  TAccountStakingVault,
  TAccountOwnerTokenAccount,
  TAccountOwner,
  TAccountTokenProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    stakingAccount: { value: input.stakingAccount ?? null, isWritable: true },
    stakingConfig: { value: input.stakingConfig ?? null, isWritable: false },
    stakingVault: { value: input.stakingVault ?? null, isWritable: true },
    ownerTokenAccount: {
      value: input.ownerTokenAccount ?? null,
      isWritable: true,
    },
    owner: { value: input.owner ?? null, isWritable: true },
    tokenProgram: { value: input.tokenProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Resolve default values.
  if (!accounts.tokenProgram.value) {
    accounts.tokenProgram.value =
      "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA" as Address<"TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.stakingAccount),
      getAccountMeta(accounts.stakingConfig),
      getAccountMeta(accounts.stakingVault),
      getAccountMeta(accounts.ownerTokenAccount),
      getAccountMeta(accounts.owner),
      getAccountMeta(accounts.tokenProgram),
    ],
    data: getUnstakeGhostInstructionDataEncoder().encode({}),
    programAddress,
  } as UnstakeGhostInstruction<
    TProgramAddress,
    TAccountStakingAccount,
    TAccountStakingConfig,
    TAccountStakingVault,
    TAccountOwnerTokenAccount,
    TAccountOwner,
    TAccountTokenProgram
  >);
}

export type ParsedUnstakeGhostInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    stakingAccount: TAccountMetas[0];
    stakingConfig: TAccountMetas[1];
    stakingVault: TAccountMetas[2];
    ownerTokenAccount: TAccountMetas[3];
    owner: TAccountMetas[4];
    tokenProgram: TAccountMetas[5];
  };
  data: UnstakeGhostInstructionData;
};

export function parseUnstakeGhostInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedUnstakeGhostInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 6) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      stakingAccount: getNextAccount(),
      stakingConfig: getNextAccount(),
      stakingVault: getNextAccount(),
      ownerTokenAccount: getNextAccount(),
      owner: getNextAccount(),
      tokenProgram: getNextAccount(),
    },
    data: getUnstakeGhostInstructionDataDecoder().decode(instruction.data),
  };
}
